import { getMonitorStatusOutlineColorHex, MonitorStatus } from '@fe/core';
import type { SortDescriptor } from '@progress/kendo-data-query';
import { type CSSProperties, useCallback } from 'react';
import { UDViewTagSelectionEnum } from '../../../udView/udview';
import { dashboardFlightFilters } from '../../utils/dashboard';
import type { DashboardCustomColumn } from './DashboardFlightsPanel';
import { type DashboardFlightColumn, DashboardFlightsPanel } from './DashboardFlightsPanel';

const columns: DashboardFlightColumn[] = [
    { id: 'acid', field: 'normalizedAcid', title: 'Aircraft ID', tagSelector: UDViewTagSelectionEnum.Acid },
    { id: 'acType', field: 'aircraftType', title: 'AcType', tagSelector: UDViewTagSelectionEnum.AircraftType },
    { id: 'origin', field: 'origin', title: 'Origin', tagSelector: UDViewTagSelectionEnum.Origin },
    { id: 'destination', field: 'destination', title: 'Dest', tagSelector: UDViewTagSelectionEnum.Destination }
];

const customColumns: DashboardCustomColumn[] = [
    { id: 'taxiTime', field: 'taxiTime', title: 'Taxi time' },
    { id: 'taxiTypeFormatted', field: 'taxiTypeFormatted', title: 'Arr/Dep' },
    { id: 'etaEtd', field: 'etaEtd', title: 'ETA/ETD' }
];

const defaultSort: SortDescriptor[] = [{ field: 'taxiTime', dir: 'desc' }];

const highlightedStatuses = new Set<MonitorStatus>([
    MonitorStatus.info,
    MonitorStatus.watch,
    MonitorStatus.warning,
    MonitorStatus.critical,
    MonitorStatus.emergency
]);

type TaxiThresholdRow = {
    taxiTimeMonitorStatus?: MonitorStatus | null;
};

export function TaxiTimesFlightsList() {
    const getCellStyle = useCallback(
        (column: DashboardFlightColumn | DashboardCustomColumn, _value: unknown, row: TaxiThresholdRow): CSSProperties | undefined => {
            if (column.id !== 'taxiTime') return undefined;

            const status = row?.taxiTimeMonitorStatus;
            if (status == null || !highlightedStatuses.has(status)) return undefined;

            const color = getMonitorStatusOutlineColorHex(status);
            return {
                backgroundColor: `color-mix(in srgb, ${color} 25%, var(--kendo-color-surface))`,
                color: 'var(--kendo-color-on-surface)',
                fontWeight: 700,
                boxShadow: `inset 0 -2px 0 ${color}`
            };
        },
        []
    );

    return (
        <DashboardFlightsPanel
            title="Taxiing flights"
            customColumns={customColumns}
            flightFilters={dashboardFlightFilters['taxi-times']}
            columns={columns}
            defaultSort={defaultSort}
            variant="taxi-times"
            emptyText="No taxiing flights"
            getCellStyle={getCellStyle}
            includeTaxiTimeMonitorStatus
        />
    );
}
